import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LogoType, logoUrls } from "@/app/Tenants/lookupTenantTheme";
import { cn } from "@/lib/utils";
import { TenantDetails } from "@/store/authStore";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";
import TenantLogoSpinner from "../Tenants/TenantLogoSpinner";

interface TenantSelectorProps {
    onConfirm: (tenant: TenantDetails | null) => void;
    open: boolean;
    tenantOptions: TenantDetails[];
}

export default function TenantSelector({ open, onConfirm, tenantOptions }: TenantSelectorProps) {

    const [value, setValue] = useState<TenantDetails | null>(null);
    useEffect(() => {
        const lastTenantId = localStorage.getItem('lastTenantId');
        const lastTenant = tenantOptions.find(x => x.tenantId == lastTenantId) ?? null;
        if (lastTenant) {
            setValue(lastTenant);

        }
    }, [tenantOptions])
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [sentTenant, setSentTenant] = useState<TenantDetails | null>(null);

    function selectTenant(tenant: TenantDetails) {
        localStorage.setItem('lastTenantId', tenant.tenantId);
        setSentTenant(tenant);
        onConfirm(tenant);
    }

    return (
        <Dialog open={open} onOpenChange={() => { if (open) { onConfirm(sentTenant) } }}>
            <DialogContent onCloseAutoFocus={() => { onConfirm(null) }} >
                <DialogHeader>
                    <DialogTitle>Select a tenant</DialogTitle>
                    <DialogDescription className="p-0 pt-6">
                        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={open}
                                    className="w-[400px] justify-between"
                                >
                                    <span>
                                        {value && (
                                            <img className="inline h-4 w-8 object-contain" src={logoUrls(value)[LogoType.DarkMini]} />
                                        )}
                                        <span className="px-2">
                                            {value
                                                ? value.tenantName
                                                : "Select a tenant..."}
                                        </span>
                                    </span>

                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-[400px] p-0 overflow-hidden"
                                align="start"
                                onWheel={(e) => e.stopPropagation()}
                            >
                                <Command>
                                    <CommandInput placeholder="Search tenants..." />
                                    <CommandList className="max-h-[300px] overflow-y-auto overscroll-contain will-change-scroll touch-auto">
                                        <CommandEmpty>No Tenants Found.</CommandEmpty>
                                        <CommandGroup>  
                                            {tenantOptions.map((tenant) => (
                                                <CommandItem
                                                    key={tenant.tenantId}
                                                    value={tenant.tenantName}
                                                    className="flex justify-between"
                                                    onSelect={() => {
                                                        setValue(tenantOptions.find((ext_tenant) => ext_tenant.tenantId === tenant.tenantId) || null)
                                                        setPopoverOpen(false)
                                                    }}
                                                >
                                                    <span>
                                                        <TenantLogoSpinner className="inline h-4 w-8 object-contain" tenantDetails={tenant} logoType={LogoType.DarkMini} />
                                                        <span className="px-2">{tenant.tenantName}</span>
                                                    </span>
                                                    <Check
                                                        className={cn(
                                                            "mr-2 h-4 w-4",
                                                            value?.tenantId === tenant.tenantId ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                <Button type="submit" disabled={!value} onClick={() => { selectTenant(value ?? tenantOptions[0]) }}>Confirm</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}